import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Palette, Type, RotateCcw } from 'lucide-react';

// Preset color themes
const COLOR_PRESETS = [
  { name: 'Professional Blue', primary: '#1e40af', accent: '#3b82f6', headerBg: '#f8fafc' },
  { name: 'Modern Teal', primary: '#0d9488', accent: '#14b8a6', headerBg: '#f0fdfa' },
  { name: 'Classic Navy', primary: '#1e3a5f', accent: '#2563eb', headerBg: '#f1f5f9' },
  { name: 'Forest Green', primary: '#166534', accent: '#22c55e', headerBg: '#f0fdf4' },
  { name: 'Royal Purple', primary: '#6b21a8', accent: '#a855f7', headerBg: '#faf5ff' },
  { name: 'Warm Orange', primary: '#c2410c', accent: '#f97316', headerBg: '#fff7ed' },
  { name: 'Classic Black', primary: '#171717', accent: '#525252', headerBg: '#fafafa' },
];

// Font options
const FONT_OPTIONS = [
  { value: 'system', label: 'System Default', family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { value: 'inter', label: 'Inter', family: '"Inter", sans-serif' },
  { value: 'roboto', label: 'Roboto', family: '"Roboto", sans-serif' },
  { value: 'open-sans', label: 'Open Sans', family: '"Open Sans", sans-serif' },
  { value: 'lato', label: 'Lato', family: '"Lato", sans-serif' },
  { value: 'poppins', label: 'Poppins', family: '"Poppins", sans-serif' },
  { value: 'playfair', label: 'Playfair Display', family: '"Playfair Display", serif' },
  { value: 'merriweather', label: 'Merriweather', family: '"Merriweather", serif' },
  { value: 'georgia', label: 'Georgia', family: 'Georgia, serif' },
  { value: 'times', label: 'Times New Roman', family: '"Times New Roman", Times, serif' },
];

const DEFAULT_THEME = {
  primary_color: '#1e40af',
  accent_color: '#3b82f6',
  header_bg_color: '#f8fafc',
  text_color: '#1e293b',
  font_family: 'system',
};

export default function InvoiceThemeDialog({ open, onOpenChange, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState({ ...DEFAULT_THEME });

  useEffect(() => {
    if (open) {
      loadTheme();
    }
  }, [open]);

  const loadTheme = async () => {
    setLoading(true);
    try {
      const response = await api.getInvoiceSettings();
      const savedTheme = response.data.theme || {};
      setTheme({
        primary_color: savedTheme.primary_color || DEFAULT_THEME.primary_color,
        accent_color: savedTheme.accent_color || DEFAULT_THEME.accent_color,
        header_bg_color: savedTheme.header_bg_color || DEFAULT_THEME.header_bg_color,
        text_color: savedTheme.text_color || DEFAULT_THEME.text_color,
        font_family: savedTheme.font_family || DEFAULT_THEME.font_family,
      });
    } catch (error) {
      console.error('Failed to load theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Get current settings and merge theme
      const currentSettings = await api.getInvoiceSettings();
      await api.updateInvoiceSettings({
        ...currentSettings.data,
        theme: theme
      });
      toast.success('Invoice theme saved');
      onSave?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setTheme(prev => ({
      ...prev,
      primary_color: preset.primary,
      accent_color: preset.accent,
      header_bg_color: preset.headerBg,
    }));
  };

  const resetToDefault = () => {
    setTheme({ ...DEFAULT_THEME });
    toast.info('Theme reset to default');
  };

  const getFontFamily = (fontValue) => {
    const font = FONT_OPTIONS.find(f => f.value === fontValue);
    return font?.family || FONT_OPTIONS[0].family;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Invoice Theme
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Customize the colors and fonts for your invoices.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Color Presets */}
            <div className="space-y-3">
              <Label className="text-slate-300 flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Quick Presets
              </Label>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="group relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-white/50 transition-all"
                    title={preset.name}
                  >
                    <div 
                      className="absolute inset-0" 
                      style={{ backgroundColor: preset.headerBg }}
                    />
                    <div 
                      className="absolute bottom-0 left-0 right-0 h-1/2" 
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="absolute bottom-1 right-1 w-3 h-3 rounded-full border-2 border-white"
                      style={{ backgroundColor: preset.accent }}
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Click a preset to apply its colors</p>
            </div>

            {/* Custom Colors */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                <Palette className="h-4 w-4" />
                Custom Colors
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.primary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-600"
                    />
                    <input
                      type="text"
                      value={theme.primary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="flex-1 bg-slate-800 border-slate-700 text-white text-xs rounded px-2 py-1.5 uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Accent Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.accent_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-600"
                    />
                    <input
                      type="text"
                      value={theme.accent_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="flex-1 bg-slate-800 border-slate-700 text-white text-xs rounded px-2 py-1.5 uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Header Background</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.header_bg_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, header_bg_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-600"
                    />
                    <input
                      type="text"
                      value={theme.header_bg_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, header_bg_color: e.target.value }))}
                      className="flex-1 bg-slate-800 border-slate-700 text-white text-xs rounded px-2 py-1.5 uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Text Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.text_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, text_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-600"
                    />
                    <input
                      type="text"
                      value={theme.text_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, text_color: e.target.value }))}
                      className="flex-1 bg-slate-800 border-slate-700 text-white text-xs rounded px-2 py-1.5 uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Font Selection */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                <Type className="h-4 w-4" />
                Typography
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Font Family</Label>
                <Select 
                  value={theme.font_family} 
                  onValueChange={(value) => setTheme(prev => ({ ...prev, font_family: value }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {FONT_OPTIONS.map(font => (
                      <SelectItem 
                        key={font.value} 
                        value={font.value} 
                        className="text-white"
                        style={{ fontFamily: font.family }}
                      >
                        {font.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Live Preview */}
            <div className="space-y-3">
              <Label className="text-slate-300">Preview</Label>
              <div 
                className="rounded-lg overflow-hidden border border-slate-600"
                style={{ fontFamily: getFontFamily(theme.font_family) }}
              >
                {/* Preview Header */}
                <div 
                  className="p-4 text-center"
                  style={{ backgroundColor: theme.header_bg_color }}
                >
                  <h3 
                    className="text-lg font-bold"
                    style={{ color: theme.primary_color }}
                  >
                    Your Company Name
                  </h3>
                  <p className="text-xs" style={{ color: theme.text_color, opacity: 0.6 }}>
                    Professional Process Server
                  </p>
                </div>
                
                {/* Preview Meta Bar */}
                <div 
                  className="flex justify-around py-2 border-t border-b text-center text-xs"
                  style={{ 
                    backgroundColor: theme.header_bg_color, 
                    borderColor: theme.accent_color + '40',
                    color: theme.text_color 
                  }}
                >
                  <div>
                    <div className="opacity-50 text-[10px]">INVOICE #</div>
                    <div className="font-semibold">INV-001</div>
                  </div>
                  <div style={{ borderLeft: `1px solid ${theme.accent_color}40` }} className="pl-4">
                    <div className="opacity-50 text-[10px]">ISSUE DATE</div>
                    <div className="font-semibold">Feb 23, 2026</div>
                  </div>
                  <div style={{ borderLeft: `1px solid ${theme.accent_color}40` }} className="pl-4">
                    <div className="opacity-50 text-[10px]">DUE DATE</div>
                    <div className="font-semibold">Mar 25, 2026</div>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="p-4 bg-white" style={{ color: theme.text_color }}>
                  <div className="flex justify-between text-xs mb-3">
                    <div>
                      <div className="opacity-50 text-[10px] uppercase">From</div>
                      <div 
                        className="border-l-2 pl-2 mt-1"
                        style={{ borderColor: theme.accent_color }}
                      >
                        <div className="font-semibold">Your Name</div>
                        <div className="opacity-60">123 Main Street</div>
                      </div>
                    </div>
                    <div>
                      <div className="opacity-50 text-[10px] uppercase">Bill To</div>
                      <div 
                        className="border-l-2 pl-2 mt-1"
                        style={{ borderColor: theme.primary_color }}
                      >
                        <div className="font-semibold">Client Name</div>
                        <div className="opacity-60">456 Client Ave</div>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className="text-[10px] font-semibold uppercase border-b pb-1 mb-2"
                    style={{ borderColor: theme.primary_color, color: theme.primary_color }}
                  >
                    Services & Charges
                  </div>
                  
                  <div className="flex justify-between text-xs py-1">
                    <span>Process Service</span>
                    <span className="font-semibold">$85.00</span>
                  </div>
                  
                  <div 
                    className="flex justify-between text-sm font-bold pt-2 mt-2 border-t"
                    style={{ borderColor: theme.primary_color }}
                  >
                    <span>Total</span>
                    <span style={{ color: theme.primary_color }}>$85.00</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4 border-t border-slate-700">
              <Button
                type="button"
                variant="outline"
                onClick={resetToDefault}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Saving...' : 'Save Theme'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
